import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';

function authHeaders(): Record<string, string> {
  const token = getTokens()?.token;
  return {
    'Content-Type': 'application/json',
    // TCAPI credential endpoints require andromeda permissions, which are only
    // available on internal orgs. RapidSOS Admin is the internal org that all
    // devbox admin users belong to.
    'X-RapidSOS-Org': 'RapidSOS Admin',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function sendLocation({
  latitude,
  longitude,
  phoneNumber,
  source = 'CALL',
}: {
  latitude: number;
  longitude: number;
  phoneNumber: string;
  source?: string;
}) {
  const url = `${getAPIHost()}/v2/els/location`;
  const now = Date.now();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: new URLSearchParams({
      v: '2',
      device_number: phoneNumber,
      time: `${now}`,
      location_time: `${now}`,
      location_latitude: `${latitude}`,
      location_longitude: `${longitude}`,
      location_accuracy: '2.5',
      source,
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
}

/**
 * Triggers Shanghai's mock TCAPI server to push a callPresented event
 * to the text_to_911 service, creating a real database session.
 */
export async function simulateTcapiCall(
  phoneNumber: string,
): Promise<{ callId: string; status: string }> {
  const url = `${getAPIHost()}/shanghai/tcapi_mock/simulate-call`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Shanghai mock TCAPI error (${response.status}): ${text}`);
  }
  return await response.json();
}

/**
 * Triggers Shanghai's mock TCAPI server to push a messageReceived event
 * to the text_to_911 service for an existing call.
 */
export async function simulateTcapiMessage(callId: string, message: string) {
  const url = `${getAPIHost()}/shanghai/tcapi_mock/simulate-message`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ callId, message }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Shanghai mock TCAPI error (${response.status}): ${text}`);
  }
  return await response.json();
}

/**
 * Fetches existing TCAPI credentials for the given authority account.
 */
export async function getTcapiCredentials(
  accountId: string,
): Promise<{ id: string; tcapiAgencyId: string }[]> {
  const url = `${getAPIHost()}/v1/text_to_911/tcapi/credentials/${accountId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to fetch credentials (${response.status}): ${text}`,
    );
  }
  return await response.json();
}

/**
 * Seeds a mock TCAPI credential for the given authority account
 * using the existing text_to_911 batch credentials API.
 */
export async function seedTcapiCredential(accountId: string) {
  const url = `${getAPIHost()}/v1/text_to_911/tcapi/credentials/${accountId}/batch`;

  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      add: [
        {
          agency_id: 'shanghai-mock-agency',
          secret_key: 'mock-secret-key',
          user_pass: 'mock-user:mock-password',
        },
      ],
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to seed credential (${response.status}): ${text}`);
  }
  return await response.json();
}
